import { useEffect } from 'react';

import { getRightSidebarFollowerStyle, OPEN_MODAL_DOCUMENT_ATTR } from 'cpool-components/general/Modal/utils';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LuBug } from 'react-icons/lu';

import { useSentryFeedback } from '~/hooks/useSentryFeedback';
import { cn } from '~/utils/cnTailwind';

const FADE_TRANSITION = 'padding 200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms ease-out';

/**
 * Stands in for the header button, which a modal covers. Visibility is keyed off
 * {@link OPEN_MODAL_DOCUMENT_ATTR} in CSS so showing it does not re-render React
 * during the panel slide.
 */
export const ReportBugFloatingButton: React.FC = () => {
    const { t } = useTranslation();
    const { elementRef, isAvailable } = useSentryFeedback<HTMLButtonElement>();
    const sidebarFollowerStyle = getRightSidebarFollowerStyle();

    useEffect(() => {
        const button = elementRef.current;
        if (!button) {
            return undefined;
        }

        const syncInert = () => {
            button.inert = !document.documentElement.hasAttribute(OPEN_MODAL_DOCUMENT_ATTR);
        };

        const observer = new MutationObserver(syncInert);
        observer.observe(document.documentElement, {
            attributeFilter: [OPEN_MODAL_DOCUMENT_ATTR],
            attributes: true,
        });
        syncInert();

        return () => observer.disconnect();
    }, [elementRef, isAvailable]);

    if (isAvailable === false) {
        return null;
    }

    // Keep the button outside Headless UI dialogs so its clicks do not close an open modal.
    return createPortal(
        <Button
            ref={elementRef}
            variant={ButtonVariant.SECONDARY}
            data-testid="report-bug-floating-button"
            data-cpool-report-bug-floating=""
            style={{
                ...sidebarFollowerStyle,
                transition: FADE_TRANSITION,
            }}
            className={cn(
                'group fixed bottom-4 right-4 z-[9991]',
                'flex items-center overflow-hidden',
                'rounded-full bg-white px-3 py-3 text-midnight border border-grey-light shadow-[0_2px_6px_0_rgba(163,177,196,0.20)]',
                'pointer-events-none opacity-0',
                // Drop Button's `transition-colors` so it cannot fight the inline transform.
                'transition-[padding,opacity]',
                'hover:px-4 gap-0',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-midnight',
            )}
            aria-label={t('COMMON.REPORT_A_BUG')}
        >
            <LuBug className="size-5 shrink-0" aria-hidden />
            <span
                className={cn(
                    'max-w-0 overflow-hidden whitespace-nowrap text-sm opacity-0',
                    'transition-all ease-out duration-150',
                    'group-hover:ml-2 group-hover:max-w-xs group-hover:opacity-100',
                )}
            >
                {t('COMMON.REPORT_A_BUG')}
            </span>
        </Button>,
        document.body,
    );
};
